import React, { useCallback, useEffect, useState } from 'react';
import { RotateCcw } from 'lucide-react';

import {
  loadTranslationEvent,
  loadTranslationEvents,
  rollbackTranslationEvent,
} from './api';
import { Empty, Failed, Loading } from './parts';
import { errorMessage } from './ui';
import { wordDiff } from './wordDiff';

const KIND_LABEL = {
  direct: 'Single edit',
  bulk: 'Spreadsheet upload',
  rollback: 'Rollback',
};

// Same marking the edit history uses, so a change reads the same in both places.
function Diff({ before, after }) {
  return (
    <p className="xo-translation-diff">
      {wordDiff(before ?? '', after ?? '').map((part, index) => {
        const key = `${index}-${part.type}`;
        if (part.type === 'del') return <del key={key}>{part.text}</del>;
        if (part.type === 'ins') return <ins key={key}>{part.text}</ins>;
        return <span key={key}>{part.text}</span>;
      })}
    </p>
  );
}

// A create has nothing to diff against and a removal has nothing left, so each
// shows the value it concerns rather than an empty comparison.
function Change({ change }) {
  if (change.operation === 'create') {
    return (
      <>
        <p className="xomics-field-hint">Filled in, previously untranslated</p>
        <p className="xo-translation-diff"><ins>{change.newValue}</ins></p>
      </>
    );
  }
  if (change.operation === 'remove') {
    return (
      <>
        <p className="xomics-field-hint">Removed, back to the English fallback</p>
        <p className="xo-translation-diff"><del>{change.previousValue}</del></p>
      </>
    );
  }
  return <Diff before={change.previousValue} after={change.newValue} />;
}

function when(value) {
  return new Date(Number(value)).toLocaleString(undefined, {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

// The change set of one event, paged. A bulk upload can hold tens of thousands,
// so this never tries to hold them all.
function Changes({ uuid }) {
  const [changes, setChanges] = useState([]);
  const [afterId, setAfterId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchPage = useCallback(async (cursor) => {
    setLoading(true);
    setError('');
    try {
      const page = await loadTranslationEvent(uuid, { afterId: cursor });
      setChanges((current) => (cursor === null ? page.changes : [...current, ...page.changes]));
      setAfterId(page.hasMore ? page.nextAfterId : null);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  useEffect(() => { fetchPage(null); }, [fetchPage]);

  if (error) return <Failed message={error} onRetry={() => fetchPage(null)} />;
  if (loading && changes.length === 0) return <Loading label="Reading the change set" />;

  return (
    <>
      <table className="xo-table xo-event-changes">
        <thead>
          <tr><th>Key</th><th>Changes</th></tr>
        </thead>
        <tbody>
          {changes.map((change) => (
            <tr key={change.id}>
              <td><code>{change.translationKey}</code></td>
              <td><Change change={change} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {afterId !== null && (
        <button type="button" className="xo-btn-2" disabled={loading}
          onClick={() => fetchPage(afterId)}>
          {loading ? 'Loading…' : 'Load more changes'}
        </button>
      )}
    </>
  );
}

function Event({ event, expanded, onToggle, onRollback, rollingBack }) {
  const reverted = event.revertedByUuid !== null;
  const isRollback = event.revertsUuid !== null;

  return (
    <li className={reverted ? 'xo-event is-reverted' : 'xo-event'}>
      <div className="xo-event-head">
        <div>
          <p className="xomics-field-hint">
            {event.actor?.name ?? 'System'} · {when(event.createdAt)}
          </p>
          <p className="xomics-field-hint">
            {event.contentTypeName ?? event.contentTypeCode} · {event.languageCode}
          </p>
          <button type="button" className="xo-link" aria-expanded={expanded}
            onClick={() => onToggle(event.uuid)}>
            {KIND_LABEL[event.kind] ?? event.kind}
            {' · '}
            {event.changeCount.toLocaleString()}
            {event.changeCount === 1 ? ' change' : ' changes'}
          </button>
          {event.source && <p className="xomics-field-hint">{event.source}</p>}
          {isRollback && <p className="xomics-field-hint">Undoes an earlier event</p>}
          {reverted && <p className="xomics-field-hint">Already rolled back</p>}
        </div>

        {!reverted && (
          <button type="button" className="xo-btn-2" disabled={rollingBack}
            onClick={() => onRollback(event)}>
            <RotateCcw aria-hidden="true" />
            {rollingBack ? 'Rolling back…' : 'Roll back'}
          </button>
        )}
      </div>

      {expanded && <Changes uuid={event.uuid} />}
    </li>
  );
}

// Every edit, newest first, across all content types and languages. A
// spreadsheet upload has to stay findable whatever is selected above, so this
// is not scoped to the picker. Rolling one back writes a new event rather than
// erasing anything, and the server refuses if a later edit would be overwritten.
export default function TranslationEventLog({ onRolledBack }) {
  const [events, setEvents] = useState([]);
  const [beforeId, setBeforeId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paging, setPaging] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [rollingBack, setRollingBack] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const page = await loadTranslationEvents({});
      setEvents(page.events);
      setBeforeId(page.hasMore ? page.nextBeforeId : null);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { setStatus(''); setExpanded(null); fetchEvents(); }, [fetchEvents]);

  const older = useCallback(async () => {
    if (beforeId === null) return;
    setPaging(true);
    try {
      const page = await loadTranslationEvents({ beforeId });
      setEvents((current) => [...current, ...page.events]);
      setBeforeId(page.hasMore ? page.nextBeforeId : null);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setPaging(false);
    }
  }, [beforeId]);

  const toggle = useCallback((uuid) => {
    setExpanded((current) => (current === uuid ? null : uuid));
  }, []);

  const rollback = useCallback(async (event) => {
    setRollingBack(event.uuid);
    setError('');
    setStatus('');
    try {
      const result = await rollbackTranslationEvent(event.uuid);
      setStatus(`Rolled back ${event.changeCount.toLocaleString()}`
        + `${event.changeCount === 1 ? ' change' : ' changes'}.`
        + ' The original event is still in this list.');
      await fetchEvents();
      if (typeof onRolledBack === 'function') onRolledBack(result);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setRollingBack(null);
    }
  }, [fetchEvents, onRolledBack]);

  if (loading && events.length === 0) return <Loading label="Reading the event log" />;
  if (error && events.length === 0) return <Failed message={error} onRetry={fetchEvents} />;

  return (
    <div className="xo-event-log">
      {status && <p className="xo-alert t-ok" role="status">{status}</p>}
      {error && <Failed message={error} />}

      {events.length === 0 ? (
        <Empty
          title="No edits recorded yet"
          detail="Every edit from here on is recorded as an event that can be rolled back."
        />
      ) : (
        <ol className="xo-events">
          {events.map((event) => (
            <Event
              key={event.uuid}
              event={event}
              expanded={expanded === event.uuid}
              onToggle={toggle}
              onRollback={rollback}
              rollingBack={rollingBack === event.uuid}
            />
          ))}
        </ol>
      )}

      <div className="xo-foot-split">
        <span>{events.length.toLocaleString()} loaded</span>
        {beforeId !== null && (
          <button type="button" className="xo-btn-2" disabled={paging} onClick={older}>
            {paging ? 'Loading…' : 'Load older'}
          </button>
        )}
      </div>
    </div>
  );
}
